import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, CardTitle, Col, FormGroup, Input, Label, Row, Spinner } from "reactstrap";
import * as Yup from "yup";
import Validation from "../../../../utils/validation";
import { formatCurrency } from "../../../../utils/converts";
import { actions as actionsSite } from "../../../../redux/actions/site";
import "../../consultas.scss";
import "./styles.scss";
import { cnpjMask, dateMask } from "../../../../utils/mask";
import Error from "../../../../components/messagesModal/error";
import Success from "../../../../components/messagesModal/success";
import RetryQuery from "../../../../components/messagesModal/retryQuery";
import ErrorZeroBalance from "../../../../components/messagesModal/errorZeroBalance";
import BaseService from "../../../../services/baseService";
import SelectTipoContratoSophus from "../../../../components/Select/SelectTipoContratoSophus";

const baseService = new BaseService("consulta");

const NegativacaoBase3Pj = () => {
  new Validation(Yup);
  const profile = useSelector((state) => state.auth.profile);
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(null);
  const [price, setPrice] = useState(null);

  const getQuery = useCallback(async () => {
    const code = location.pathname.split("/").pop();
    const query = profile.permissionsQueries.find((q) => q.code === code);
    return query;
  },[location, profile]);

  const getQueryPrice = useCallback(async (query) => {
    const _query = profile.clientPrice.queries.find(price => price.query === query._id);
    return _query.price;
  },[profile]);

  useEffect(() => {
    const run = async () => {
      const _query = await getQuery();

      if (_query) {
        const _price = await getQueryPrice(_query);
        setQuery(_query);
        setPrice(_price);
        setLoading(false);
      } else {
        const configModal = {
          component: <Error msg={"Consulta não encontrada"} />,
          title: `Erro`,
        };
        dispatch(actionsSite.setModal(configModal));
        history.push("/dashboard");
        setLoading(false);
      }
    };

    run();
  }, [location, history, getQueryPrice, getQuery, dispatch]);

  const onlyNumbers = (value) => {
    return value.replace(/[^\d]/g, '');
  }

  const [formData, setFormData] = useState({
    cnpj: '',
    tipo_contrato: '',
    contrato: '',
    data_vencimento: '',
    numero_resposta: ''
  });

  const [errors, setErrors] = useState({
    cnpj: '',
    tipo_contrato: '',
    contrato: '',
    data_vencimento: '',
    numero_resposta: ''
  });

  const setFieldValue = (name, value) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validationSchema = Yup.object().shape({
    cnpj: Yup.string().required("CNPJ é obrigatório").cpfCnpj("CNPJ inválido"),
    data_vencimento: Yup.string()
      .required("Data de Vencimento é obrigatória")
      .matches(/^\d{2}\/\d{2}\/\d{4}$/, 'Formato de data inválido (DD/MM/YYYY)')
      .test('isValidDate', 'Data inválida', function (value) {
        if (!value) return true;
        const [day, month, year] = value.split('/').map(Number);

        if (
          day === 0 ||
          day > 31 ||
          (month === 2 && day > 29) ||
          month === 0 ||
          month > 12 ||
          year < 1900 ||
          year > new Date().getFullYear()
        ) {
          return false;
        }

        const dateObject = new Date(year, month - 1, day);

        return (
          dateObject.getFullYear() === year &&
          dateObject.getMonth() === month - 1 &&
          dateObject.getDate() === day
        );
      }),
    tipo_contrato: Yup.string().required('Tipo Contrato é obrigatório').uppercase().oneOf(["AD", "AG", "AL", "AR", "C1", "C2", "C3", "C4", "C5", "C6", "CA", "CB", "CC", "CH", "CI", "CO", "CP", "CR", "CS", "CT", "DC", "DB", "DE", "DP", "EC", "EE"], "Tipo Contrato inválido"),
    contrato: Yup.string().required("Contrato é obrigatório"),
    numero_resposta: Yup.string().required("Número Resposta é obrigatório"),
  });

  const validate = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });

      setErrors({});
      await execute();
      return true;
    } catch (error) {
      const validationErrors = {};
      error.inner.forEach((err) => {
        validationErrors[err.path] = err.message;
      });

      setErrors(validationErrors);

      return false;
    }
  };

  const goOrderPayment = useCallback(() => {
    dispatch(actionsSite.closeModal());
    history.push("/financeiro/ordens-de-pagamento");
  },[dispatch, history]);

  const showMessageModal = useCallback(({ error, msg, icon=null, loadingCar=false }) => {
    const CompError = msg.includes('Sem saldo para realizar') ? ErrorZeroBalance : Error ;
    const configModal = {
      component: error ? <CompError msg={msg} goOrderPayment={goOrderPayment} /> : <Success msg={msg} icon={icon} loadingCar={loadingCar} />,
      title: error ? `Erro` : `Sucesso`,
    };
    dispatch(actionsSite.setModal(configModal));
  },[dispatch, goOrderPayment]);

  const execute = async () => {
    try {
      showMessageModal({ error: false, msg: "Cancelando Negativação, por favor aguarde" });

      const payload = {
        cnpj: formData.cnpj.replace(/\D/g, ''),
        data_vencimento: formData.data_vencimento.replace(/\D/g, ''),
        tipo_contrato: formData.tipo_contrato,
        contrato: formData.contrato,
        numero_resposta: formData.numero_resposta
      }

      baseService._postConsulta(`${baseService.route}/${query.code}`, payload)
        .then((res) => {
          if (res.success === false && res.duplicatedQuery) {
            const configModal = {
              component: <RetryQuery logquery={res.logqueryOrigin} msg={res.msg} execute={execute} values={payload}/>,
              title: 'Suspeita de consulta em duplicidade',
            };
            dispatch(actionsSite.setModal(configModal));
            return;
          }

          showMessageModal({ error: !res.success, msg: res.msg });
          if (res.success) {
            history.push("/historico/" + res.logQuery);
          }
        })
        .catch((error) => {
          if (error.response) {
            showMessageModal({ error: true, msg: error.response.data.msg })
          }
        });
    } catch (e) {
      console.log(e.toString());
    }
  };

  return (
    <Card>
      <CardBody>
        {loading ? (
          <div className="text-center">
            <Spinner color="danger" />
          </div>
        ) : (
          <form onSubmit={(event) => { event.preventDefault() }}>
            <Row>
              <Col xs="12">
                <CardTitle>
                  <i className={`${query.icon} mr-1`}></i>
                  {query.isBlocked ?
                  <>
                    <del className="mr-2">
                      {query.type} - {query.name}
                    </del>
                    <span className="text-danger">
                      - EM MANUTENÇÃO
                    </span>
                  </>
                  :
                    query.type + ' - ' + query.name + (profile.client.showPrice ? ` | R$ ${formatCurrency(price)}` : '')
                  }
                </CardTitle>
                <div dangerouslySetInnerHTML={{ __html: query.description }} className="mb-3"/>
              </Col>
            </Row>
            <Row>
              <Col md="4" className="offset-md-8">
                <Card className="border-danger">
                  <CardHeader>
                    <h4>
                      <span className="text-danger">***</span> = Campos Obrigatórios
                    </h4>
                  </CardHeader>
                </Card>
              </Col>
            </Row>
            <Card className="border-custom">
              <CardHeader className="card-custom">
                <CardTitle className="card-text-custom">Informe os dados para Cancelamento</CardTitle>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col sm="6" md="4">
                    <FormGroup>
                      <Label htmlFor="cnpj"><span className="text-danger">***</span> CNPJ</Label>
                      <Input
                        type="text"
                        name="cnpj"
                        id="cnpj"
                        placeholder="Digite o CNPJ"
                        className={"form-control" + (errors.cnpj ? " is-invalid" : "")}
                        onChange={(e) => setFieldValue('cnpj', cnpjMask(e.target.value))}
                        value={formData.cnpj}
                        maxLength="18"
                      />
                      <div className="invalid-feedback">{errors.cnpj}</div>
                    </FormGroup>
                  </Col>

                  <Col sm="6" md="4">
                    <FormGroup>
                      <Label htmlFor="tipo_contrato"><span className="text-danger">***</span> Tipo Contrato</Label>
                      <SelectTipoContratoSophus
                        name="tipo_contrato"
                        id="tipo_contrato"
                        placeholder="Selecione o Tipo de Contrato"
                        className={errors.tipo_contrato ? " is-invalid invalid-multi-select" : ""}
                        onChange={(data) => { setFieldValue("tipo_contrato", data ? data.value : ""); }}
                        isSearchable={true}
                        isClearable={true}
                        typeOptions={"PJ"}
                      />
                      <div className="invalid-feedback">{errors.tipo_contrato}</div>
                    </FormGroup>
                  </Col>

                  <Col sm="6" md="4">
                    <FormGroup>
                      <Label htmlFor="contrato"><span className="text-danger">***</span> Contrato</Label>
                      <Input
                        type="text"
                        name="contrato"
                        id="contrato"
                        placeholder="Digite o Contrato"
                        className={"form-control" + (errors.contrato ? " is-invalid" : "")}
                        onChange={(e) => setFieldValue('contrato', e.target.value)}
                        value={formData.contrato}
                        maxLength="18"
                      />
                      <div className="invalid-feedback">{errors.contrato}</div>
                    </FormGroup>
                  </Col>

                  <Col sm="6" md="4">
                    <FormGroup>
                      <Label htmlFor="data_vencimento"><span className="text-danger">***</span> Data de Vencimento</Label>
                      <Input
                        type="text"
                        name="data_vencimento"
                        id="data_vencimento"
                        placeholder="Digite a Data de Nascimento"
                        className={"form-control" + (errors.data_vencimento ? " is-invalid" : "")}
                        onChange={(e) => setFieldValue('data_vencimento', dateMask(e.target.value))}
                        value={formData.data_vencimento}
                        maxLength="10"
                      />
                      <div className="invalid-feedback">{errors.data_vencimento}</div>
                    </FormGroup>
                  </Col>

                  <Col sm="6" md="4">
                    <FormGroup>
                      <Label htmlFor="numero_resposta"><span className="text-danger">***</span> Protocolo da Negativação</Label>
                      <Input
                        type="text"
                        name="numero_resposta"
                        id="numero_resposta"
                        placeholder="Digite o Número Resposta"
                        className={"form-control" + (errors.numero_resposta ? " is-invalid" : "")}
                        onChange={(e) => setFieldValue('numero_resposta', onlyNumbers(e.target.value))}
                        value={formData.numero_resposta}
                        maxLength="18"
                      />
                      <div className="invalid-feedback">{errors.numero_resposta}</div>
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <div className="text-right">
              <Button className="mr-1" color="primary" onClick={validate}>Enviar</Button>
            </div>
          </form>
        )}
      </CardBody>
    </Card>
  );
};

export default NegativacaoBase3Pj;
